import type { Claim, EOBExpandableRowProps } from '../types'
import type { ColumnsType } from 'antd/es/table'

import {
  CheckOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { Button, Dropdown, Modal, Skeleton, Table, Tag } from 'antd'
import cx from 'classnames'
import { useCallback, useMemo, useState } from 'react'

import $api from '../../../../API'
import { Permission } from '../../../../domain/permission.enum'
import { Role } from '../../../../domain/role.enum'
import { useAppSelector } from '../../../../store'

import { baseCellClassName, baseHeaderCellClassName } from '../constants'
import { currencyFormatter } from '../functions'
import { ClaimStatusHistoryTooltip } from './claim-status-history-tooltip'
import { EmptyCellValue } from './empty-cell-value'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

type QueryKeyWithParams<T> = T extends Array<string | infer U> ? [string, U] : never

export const EOBExpandableRow = (props: EOBExpandableRowProps): JSX.Element => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [claimToDelete, setClaimToDelete] = useState<Claim | null>(null)
  const [deleteComment, setDeleteComment] = useState('')
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [
      'check-run-claims',
      {
        checkRunId: props.checkRunId,
        groupId: props.groupId,
        paymentFileId: props.paymentFileId,
        eobNumber: props.eobNumber
      }
    ],

    async queryFn({ queryKey, signal }) {
      const [, { checkRunId, groupId, paymentFileId, eobNumber }] = queryKey as QueryKeyWithParams<
        typeof queryKey
      >

      const response = await $api.get<Claim[]>(
        `/check-runs/${checkRunId}/groups/${groupId}/payment-files/${paymentFileId}/eobs/${eobNumber}`,
        {
          signal
        }
      )

      return response.data
    }
  })

  const deleteMutation = useMutation({
    async mutationFn({ claimId, comment }: { claimId: string; comment?: string }) {
      await $api.delete(`/claims/${claimId}`, {
        data: { comment }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['check-run-claims'] })
      queryClient.invalidateQueries({ queryKey: ['check-runs'] })
      setDeleteModalOpen(false)
      setClaimToDelete(null)
      setDeleteComment('')
    }
  })

  const claims = query?.data ?? []

  const user = useAppSelector(state => state.app.user)

  const has = useCallback(
    (roles: Role[]): boolean => {
      return roles.some(role => user.roles.includes(role))
    },
    [user.roles]
  )

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return user.permissions.includes(permission)
    },
    [user.permissions]
  )

  const handleDeleteClick = useCallback((claim: Claim) => {
    setClaimToDelete(claim)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (claimToDelete) {
      deleteMutation.mutate({
        claimId: claimToDelete.id,
        comment: deleteComment || undefined
      })
    }
  }, [claimToDelete, deleteComment, deleteMutation])

  const tpaClaimMapping = useMemo(() => {
    const mapping = new Map<string, string>()

    const sortedClaims = [...(query.data ?? [])].sort((a, b) =>
      a.claim_number.localeCompare(b.claim_number)
    )

    for (const claim of sortedClaims) {
      const tpaId = claim.claim_number.slice(0, -2)
      if (!mapping.has(tpaId)) {
        mapping.set(tpaId, claim.claim_number)
      }
    }

    return mapping
  }, [query.data])

  const getEligibilityPriority = (isEligible: boolean | undefined | null): number => {
    if (isEligible === true) {
      return 0
    }

    if (isEligible === false) {
      return 1
    }

    return 2
  }

  const sortedClaims = claims.sort((a, b) => {
    const aPriority = getEligibilityPriority(a.is_eligible)
    const bPriority = getEligibilityPriority(b.is_eligible)

    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }

    return a.claim_number.localeCompare(b.claim_number)
  })

  const columns = useMemo<ColumnsType<Claim>>(
    () => [
      {
        title: 'Claim #',
        dataIndex: 'claim_number',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Eligible',
        dataIndex: 'is_eligible',
        align: 'center',

        filters: [
          {
            text: 'Yes',
            value: 'yes'
          },
          {
            text: 'No',
            value: 'no'
          },
          {
            text: '—',
            value: 'unknown'
          }
        ],

        onFilter: (value, record) => {
          if (value === 'yes') {
            return record.is_eligible === true
          }

          if (value === 'no') {
            return record.is_eligible === false
          }

          if (value === 'unknown') {
            return typeof record.is_eligible !== 'boolean'
          }

          return true
        },

        render(value: boolean) {
          if (typeof value !== 'boolean') {
            return <EmptyCellValue />
          }

          return value ? 'Yes' : 'No'
        },

        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },

        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Network',
        dataIndex: 'is_network',
        align: 'center',
        render(value: boolean) {
          if (value) {
            return <CheckOutlined />
          }
          return <></>
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      ...(has([Role.Administrator])
        ? ([
            {
              title: 'Payment Symbol',
              dataIndex: ['original', 'payment_symbol'],
              align: 'center',

              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              }
            },
            {
              title: 'Original File Name',
              dataIndex: ['original', 'filename'],
              align: 'center',

              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              }
            }
          ] as ColumnsType<Claim>)
        : []),
      {
        title: 'Payee',
        dataIndex: ['payee', 'name'],
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Provider',
        dataIndex: ['provider', 'name'],
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        align: 'right',
        render(value: number, record: Claim) {
          if (!record.is_eligible) {
            return <EmptyCellValue />
          }

          return currencyFormatter.format(value)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      ...(has([Role.Administrator])
        ? [
            {
              title: 'Status',
              dataIndex: 'status',
              align: 'center' as const,
              render(_: unknown, record: Claim) {
                const claimTpaId = record.claim_number.slice(0, -2)
                const firstClaimWithThisTpaId = tpaClaimMapping.get(claimTpaId)

                if (record.claim_number !== firstClaimWithThisTpaId) {
                  return <EmptyCellValue />
                }

                const claimStatus = record.nomi_claims_status?.find(
                  status => status.tpa_claim_id === claimTpaId
                )

                if (!claimStatus || !claimStatus.status) {
                  return <EmptyCellValue />
                }

                return (
                  <div className='flex gap-2 justify-center items-center'>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        claimStatus.status === 'Paid'
                          ? 'bg-green-100 text-green-800'
                          : claimStatus.status === 'Pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : claimStatus.status === 'Rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {claimStatus.status}
                    </span>
                    <ClaimStatusHistoryTooltip claimId={claimStatus.nomi_claim_id}>
                      <QuestionCircleOutlined className='text-sm opacity-25 transition-opacity cursor-pointer text-lucent-text hover:opacity-100' />
                    </ClaimStatusHistoryTooltip>
                  </div>
                )
              },
              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              }
            }
          ]
        : []),
      ...(hasPermission(Permission.DeleteClaims)
        ? [
            {
              dataIndex: 'actions',
              width: 40,
              align: 'center' as const,
              render(_: unknown, record: Claim) {
                return (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'delete',
                          label: 'Delete',
                          icon: <DeleteOutlined />,
                          danger: true
                        }
                      ],
                      onClick(info) {
                        info.domEvent.preventDefault()
                        info.domEvent.stopPropagation()

                        switch (info.key) {
                          case 'delete': {
                            handleDeleteClick(record)
                            break
                          }
                        }
                      }
                    }}
                  >
                    <Button
                      className='flex justify-center items-center'
                      icon={<EllipsisOutlined />}
                      type='text'
                      shape='circle'
                      size='small'
                    />
                  </Dropdown>
                )
              },
              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              }
            }
          ]
        : [])
    ],
    [tpaClaimMapping, has, hasPermission, handleDeleteClick]
  )

  const diagnosticsInfo = useMemo(() => {
    const diagnosticKeys: Array<keyof Claim['diagnostics']> = [
      'record_type',
      'claim_type',
      'clservpc',
      'run_date',
      'eep_provider_id',
      'eealp_provider_tin'
    ]

    return diagnosticKeys
      .map(key => {
        const values = [...new Set(sortedClaims.map(claim => claim.diagnostics[key]))].filter(
          Boolean
        )
        return {
          key,
          values
        }
      })
      .filter(item => item.values.length > 0)
  }, [sortedClaims])

  return (
    <>
      <div className='flex gap-4 mb-4'>
        {(diagnosticsInfo ?? []).map(({ key, values }) => (
          <div key={key} className='flex flex-col gap-1'>
            <span className='text-xs font-medium text-gray-500'>{key}:</span>

            <div className='flex gap-1'>
              {values.map(value => (
                <Tag key={value}>{value}</Tag>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Table
        size='small'
        rowKey='claim_number'
        className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
        rowClassName={(record: Claim) => {
          return !record.is_eligible ? 'opacity-50 bg-gray-50' : ''
        }}
        rowSelection={{
          columnWidth: 5.5 * 16,
          selectedRowKeys: [],
          hideSelectAll: true,

          onSelect(_, __, ___, event) {
            event.stopPropagation()
          },

          getCheckboxProps() {
            return {
              disabled: true,
              className: 'hidden'
            }
          }
        }}
        columns={
          !query.isLoading
            ? columns
            : columns.map(column => {
                return {
                  ...column,
                  render: function renderPlaceholder() {
                    return <Skeleton key={column.key} title active={true} paragraph={false} />
                  }
                }
              })
        }
        dataSource={
          !query.isLoading
            ? sortedClaims
            : ([...Array(2)].map((_, index) => ({
                id: `key${index}`
              })) as unknown as Claim[])
        }
        pagination={false}
      />

      <Modal
        title='Delete Claim'
        open={deleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false)
          setClaimToDelete(null)
          setDeleteComment('')
        }}
        confirmLoading={deleteMutation.isPending}
        okText='Delete'
        okButtonProps={{ danger: true }}
      >
        <div className='space-y-4'>
          <p>
            Are you sure you want to delete claim <strong>{claimToDelete?.claim_number}</strong>?
          </p>
          <p className='text-sm text-gray-600'>
            This action will permanently remove the claim from the database. This is typically used
            to resolve duplicate claims with different patient names.
          </p>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Comment (optional)
            </label>
            <textarea
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              rows={3}
              value={deleteComment}
              onChange={e => setDeleteComment(e.target.value)}
              placeholder='Enter a reason for deleting this claim...'
            />
          </div>
        </div>
      </Modal>
    </>
  )
}
